const React = require('react');
const { default: Styled, css } = require('styled-components');
const ComponentRegistry = require('./componentRegistry');
const ControlledFieldAdapter = require('./ControlledFieldAdapter');
const { transient$Props } = require('utils/styles');

const internals = {};

internals.resolveDecoratorStyle = (decorator, context) => {

    if (!decorator) {
        return '';
    }

    if (typeof decorator === 'function') {
        return decorator(context) || '';
    }

    return decorator;
};

module.exports = function FormField(props) {

    const { FieldRoot } = internals;
    const genericRegistry = ComponentRegistry.getGenericRegistry();

    const {
        columnGap,
        columns = 2,
        componentRegistry,
        componentsByKey,
        componentsByName,
        componentsByType,
        defaultComponent,
        field,
        fieldProps = {},
        formId,
        gap,
        genericComponentsByType = genericRegistry.genericComponentsByType,
        genericFieldAdapter = ControlledFieldAdapter
    } = props;

    if (field.type === 'hidden') {
        return null;
    }

    const resolvedComponentsByKey = componentsByKey || genericRegistry.componentsByKey;
    const resolvedComponentsByName = componentsByName || genericRegistry.componentsByName;
    const resolvedComponentsByType = componentsByType || genericRegistry.componentsByType;

    const Component = ComponentRegistry.getFieldComponent({
        field,
        componentRegistry,
        formId,
        componentsByKey: resolvedComponentsByKey,
        componentsByName: resolvedComponentsByName,
        componentsByType: resolvedComponentsByType,
        defaultComponent
    });

    const RenderableComponent = ComponentRegistry.resolveRenderableComponent({
        Component,
        componentRegistry,
        field,
        formId,
        genericFieldAdapter,
        genericComponentsByType
    });
    const formFieldStyles = internals.resolveDecoratorStyle(field.formFieldStyles, {
        Component,
        field
    });

    const fieldComponentProps = ComponentRegistry.buildFieldComponentProps({
        field,
        fieldProps,
        Component,
        componentRegistry,
        formId,
        genericComponentsByType
    });

    return <FieldRoot
        className='field-root'
        data-form-field-root='true'
        data-form-field-name={field.name}
        data-form-field-type={field.type}
        data-form-field-component={typeof field.component === 'string' ? field.component : Component?.displayName || Component?.name}
        data-userinputs-field={field.name}
        $columnGap={typeof columnGap === 'undefined' ? gap : columnGap}
        $columns={columns}
        $formFieldStyles={formFieldStyles}
        $spanSm={internals.getFieldSpan({
            columns,
            fullWidth: field.fullWidth,
            gridColumn: field.gridColumn,
            breakpoint: 'sm'
        })}
        $paddingBottom={field.gridSx?.pb}
    >
        <RenderableComponent
            {...fieldComponentProps}
        />
    </FieldRoot>;
};

internals.FieldRoot = Styled.div.withConfig(transient$Props)`
    display: flex;
    align-items: flex-start;
    width: 100%;
    min-width: 0;

    ${({ $paddingBottom, theme }) => typeof $paddingBottom !== 'undefined' ? `
        padding-bottom: ${theme.spacing($paddingBottom)};
    ` : ''}

    & > * {
        width: 100%;
        min-width: 0;
    }

    ${({ $formFieldStyles }) => css`${$formFieldStyles || ''}`}

    @media (min-width: 600px) {
        ${({ $columns, $spanSm }) => {
        const span = Math.min(Math.max($spanSm || 1, 1), Math.max(1, Number($columns) || 1));

        return `
            grid-column: span ${span};
        `;
    }}
    }
`;

internals.parseGridColumnSpan = ({
    columns = 2,
    value
}) => {

    if (!value || typeof value !== 'string') {
        return 1;
    }

    if (value.includes('-1')) {
        return columns;
    }

    const spanMatch = value.match(/\bspan\s+(\d+)/);

    if (spanMatch) {
        const span = Number(spanMatch[1]);

        return Number.isFinite(span)
            ? Math.min(columns, Math.max(1, span))
            : 1;
    }

    const matches = value.match(/\d+/g);

    if (!matches || matches.length < 2) {
        return 1;
    }

    const start = Number(matches[0]);
    const end = Number(matches[1]);

    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        return 1;
    }

    return Math.min(columns, end - start);
};

internals.getFieldSpan = ({
    columns = 2,
    fullWidth,
    gridColumn,
    breakpoint = 'sm'
}) => {

    if (fullWidth) {
        return columns;
    }

    if (!gridColumn) {
        return 1;
    }

    if (typeof gridColumn === 'string') {
        return internals.parseGridColumnSpan({
            columns,
            value: gridColumn
        });
    }

    return internals.parseGridColumnSpan({
        columns,
        value: gridColumn[breakpoint] || gridColumn.xs
    });
};

module.exports.internals = internals;
